import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { GET_ALL_PLAYERS, GET_ALL_POSTS, LLM_GATEWAY_SUMMARY_REPORT_API } from "../../../config/endpoints";
import ReactGA from 'react-ga4';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronDown,
  faUser,
  faFilePdf,
  faSpinner,
  faVideo,
  faCalendarAlt,
  faDownload,
  faLayerGroup,
  faCheck,
  faQuoteRight,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

const PlayerSelector = ({
  allPlayers,
  handlePlayerSelect,
  selectedPlayer,
  error,
  loading,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Filter players based on search term
  const filteredPlayers = allPlayers.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full max-w-md mx-auto relative z-20">
      <label className="block text-sm font-bold text-gray-700 mb-2 text-left">
        Select Athlete
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={`
            w-full px-4 py-3 bg-white text-left text-gray-700 border rounded-xl shadow-sm transition-all duration-200 flex items-center justify-between group
            ${dropdownOpen ? 'ring-2 ring-[#237e45]/20 border-[#237e45]' : 'border-gray-200 hover:border-gray-300'}
          `}
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${selectedPlayer ? 'bg-[#237e45]/10 text-[#237e45]' : 'bg-gray-100 text-gray-400'}`}>
              <FontAwesomeIcon icon={faUser} className="text-sm" />
            </div>
            <span className={`block truncate font-medium ${selectedPlayer ? 'text-gray-900' : 'text-gray-400'}`}>
              {selectedPlayer ? selectedPlayer.name : "Search for an athlete..."}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {dropdownOpen && (
          <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-3 border-b border-gray-100 bg-gray-50/50">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#237e45]/20 focus:border-[#237e45] transition-all"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="py-8 text-center text-gray-500 flex flex-col items-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[#237e45]" />
                  <span className="text-xs font-medium">Loading athletes...</span>
                </div>
              ) : filteredPlayers.length > 0 ? (
                filteredPlayers.map((player) => (
                  <div
                    key={player._id}
                    onClick={() => {
                      handlePlayerSelect(player);
                      setDropdownOpen(false);
                      setSearchTerm('');
                    }}
                    className={`
                      px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors
                      ${selectedPlayer?._id === player._id ? "bg-[#237e45]/5 text-[#237e45]" : "hover:bg-gray-50 text-gray-700"}
                    `}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${selectedPlayer?._id === player._id ? "bg-[#237e45] text-white" : "bg-gray-200 text-gray-500"}`}>
                      {player.name.charAt(0)}
                    </div>
                    <span className="font-medium">{player.name}</span>
                    {selectedPlayer?._id === player._id && (
                      <FontAwesomeIcon icon={faCheck} className="ml-auto text-sm" />
                    )}
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <p className="text-sm">No athletes found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-2 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle} /> {error}</p>}
    </div>
  );
};

export default function UsersPostsTable() {
  const navigate = useNavigate();
  const jwtToken = localStorage.getItem("BQS_TOKEN");

  const [allPlayers, setAllPlayers] = useState([]);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerPosts, setPlayerPosts] = useState([]);
  const [postPage, setPostPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const limit = 10;

  // Pageview
  useEffect(() => {
    try {
      ReactGA.send({ hitType: 'pageview', page: '/coach_flow/academy-reports', title: 'Coach Flow - Academy Reports' });
    } catch { }
  }, []);

  // Load all players when component mounts
  useEffect(() => {
    if (jwtToken && jwtToken.length >= 8) {
      loadAllPlayers();
    }
  }, [jwtToken]);

  const loadAllPlayers = async () => {
    try {
      setError(null);
      setLoading(true);

      // First get total count to calculate pages
      const initialResponse = await axios.get(
        `${GET_ALL_PLAYERS}?page=1&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }
      );

      if (initialResponse.status === 200) {
        const total = initialResponse.data.totalPlayers;
        setTotalPlayers(total);

        // Add first page of players
        let allPlayersArray = [...initialResponse.data.players];

        // Calculate how many additional pages we need to fetch
        const totalPages = Math.ceil(total / limit);

        // Fetch all remaining pages in parallel
        if (totalPages > 1) {
          const pagePromises = [];

          for (let page = 2; page <= totalPages; page++) {
            pagePromises.push(
              axios.get(
                `${GET_ALL_PLAYERS}?page=${page}&limit=${limit}`,
                {
                  headers: { Authorization: `Bearer ${jwtToken}` },
                }
              )
            );
          }

          const pageResponses = await Promise.all(pagePromises);

          // Add all players from additional pages
          pageResponses.forEach(response => {
            if (response.status === 200 && response.data.players) {
              allPlayersArray = [...allPlayersArray, ...response.data.players];
            }
          });
        }

        // Sort players by name
        allPlayersArray.sort((a, b) => a.name.localeCompare(b.name));

        // Update state with all players
        setAllPlayers(allPlayersArray);
      }
    } catch (error) {
      console.error("Error fetching all players:", error);
      setError("Failed to fetch players. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerPosts = async (playerId, postPage) => {
    if (!playerId) return;
    try {
      setError(null);
      setLoading(true);
      const response = await axios.get(
        `${GET_ALL_POSTS}?page=${postPage}&limit=5&playerId=${playerId}`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        }
      );
      if (response.status === 200) {
        setPlayerPosts((prevPosts) => [...prevPosts, ...response.data.posts]);
        setTotalPosts(response.data.totalPosts);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      setError("Failed to fetch posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    setPlayerPosts([]);
    setPostPage(1);
    setSelectedPosts(new Set());
    fetchPlayerPosts(player._id, 1);
    try { ReactGA.event({ category: 'CoachFlow', action: 'academy_player_select', label: player?.name || '' }); } catch { }
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
    try { ReactGA.event({ category: 'CoachFlow', action: 'academy_toggle_post', label: String(postId) }); } catch { }
  };

  const handleDownloadSummary = async () => {
    if (!selectedPlayer || selectedPosts.size === 0) {
      setError("Please select at least one video");
      return;
    }

    /* if (selectedPosts.size < 5) {
       setError("Please select at least 5 videos for generating the report");
       return;
     }*/

    const postIds = Array.from(selectedPosts);
    setIsDownloading(true); // Start downloading state

    try {
      const response = await axios.post(
        `${LLM_GATEWAY_SUMMARY_REPORT_API}`,
        { postIds },
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
          responseType: "blob", // Important for handling PDF response
        }
      );

      // Create a filename based on selected player name
      const playerName = selectedPlayer.name.replace(/\s+/g, '_').toLowerCase();
      const filename = `${playerName}_VISIST_Report.pdf`;

      // Create blob with the response data
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up by removing the link and revoking the object URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      try { ReactGA.event({ category: 'CoachFlow', action: 'academy_download_summary_success', label: String(postIds.length) }); } catch { }

    } catch (err) {
      console.error("Download failed", err);
      setError("Failed to generate summary report. Please try again.");
      try { ReactGA.event({ category: 'CoachFlow', action: 'academy_download_summary_error' }); } catch { }
    } finally {
      setIsDownloading(false); // End downloading state
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-4 sm:px-6 lg:px-8 font-sans text-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-extrabold text-[#237e45] mb-2 tracking-tight">
            Academy Reports
          </h1>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Generate comprehensive performance summaries and insights for your athletes.
          </p>
        </div>

        {/* Selection Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8 max-w-2xl mx-auto">
          <PlayerSelector
            allPlayers={allPlayers}
            handlePlayerSelect={handlePlayerSelect}
            selectedPlayer={selectedPlayer}
            error={error}
            loading={loading}
          />
        </div>

        {selectedPlayer && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Player Context Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  {selectedPlayer.name}
                  <span className="px-2.5 py-0.5 rounded-full bg-[#237e45]/10 text-[#237e45] text-xs font-bold uppercase tracking-wide">
                    Athlete
                  </span>
                </h2>
                <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                  <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                  <span>Coach: <span className="font-medium text-gray-700">{selectedPlayer.coaches.join(", ") || "No Coach Assigned"}</span></span>
                </div>
              </div>

              <button
                onClick={handleDownloadSummary}
                disabled={isDownloading || selectedPosts.size === 0}
                className={`
                  px-6 py-3 rounded-xl font-bold shadow-md transition-all duration-300 flex items-center gap-2 justify-center
                  ${isDownloading || selectedPosts.size === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                    : "bg-[#237e45] text-white hover:bg-[#1b6136] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95"}
                `}
              >
                {isDownloading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFilePdf} />
                    Download Summary Report
                    {selectedPosts.size > 0 && <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{selectedPosts.size}</span>}
                  </>
                )}
              </button>
            </div>

            {/* Posts Table Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <FontAwesomeIcon icon={faLayerGroup} className="text-[#237e45]" />
                  Available Sessions
                </h3>
                <div className="text-sm text-gray-500">
                  Showing <span className="font-semibold">{playerPosts.length}</span> of <span className="font-semibold">{totalPosts}</span> posts
                </div>
              </div>

              {playerPosts.length === 0 && !loading ? (
                <div className="p-12 text-center text-gray-400">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faVideo} className="text-2xl text-gray-300" />
                  </div>
                  <p>No video sessions found for this athlete.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Desktop Table View */}
                  <table className="w-full text-left border-collapse hidden md:table">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider w-16 text-center">
                          <FontAwesomeIcon icon={faCheck} />
                        </th>
                        <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Session Details</th>
                        <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="py-4 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {playerPosts.map((post, index) => {
                        const isSelected = selectedPosts.has(post.postId);
                        return (
                          <tr
                            key={index}
                            onClick={() => togglePostSelection(post.postId)}
                            className={`
                              group transition-all duration-200 cursor-pointer
                              ${isSelected ? "bg-[#237e45]/5 hover:bg-[#237e45]/10" : "hover:bg-gray-50 bg-white"}
                            `}
                          >
                            <td className="py-4 px-6 text-center">
                              <div className={`
                                w-5 h-5 rounded border mx-auto flex items-center justify-center transition-all
                                ${isSelected ? "bg-[#237e45] border-[#237e45] text-white" : "border-gray-300 bg-white group-hover:border-[#237e45]"}
                              `}>
                                {isSelected && <FontAwesomeIcon icon={faCheck} className="text-xs" />}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="font-semibold text-gray-800 group-hover:text-[#237e45] transition-colors">
                                {post.videoTitle || "Untitled Session"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-300" />
                                {post.createdAt ? new Date(post.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className={`
                                 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5
                                 ${post.state === 'released' ? 'bg-green-100 text-green-700' :
                                  post.state === 'reviewed' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'}
                               `}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50"></span>
                                {post.state}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Mobile Card View */}
                  <div className="md:hidden flex flex-col divide-y divide-gray-100">
                    {playerPosts.map((post, index) => {
                      const isSelected = selectedPosts.has(post.postId);
                      return (
                        <div
                          key={index}
                          onClick={() => togglePostSelection(post.postId)}
                          className={`
                            p-4 cursor-pointer transition-colors
                            ${isSelected ? "bg-[#237e45]/5" : "bg-white"}
                          `}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`
                              mt-1 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-all
                              ${isSelected ? "bg-[#237e45] border-[#237e45] text-white" : "border-gray-300 bg-white"}
                            `}>
                              {isSelected && <FontAwesomeIcon icon={faCheck} className="text-xs" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start mb-1">
                                <h4 className={`font-semibold text-sm truncate pr-2 ${isSelected ? "text-[#237e45]" : "text-gray-800"}`}>
                                  {post.videoTitle || "Untitled Session"}
                                </h4>
                                <span className={`
                                   px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0
                                   ${post.state === 'released' ? 'bg-green-100 text-green-700' :
                                    post.state === 'reviewed' ? 'bg-blue-100 text-blue-700' :
                                      'bg-yellow-100 text-yellow-700'}
                                 `}>
                                  {post.state}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-300" />
                                {post.createdAt ? new Date(post.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Footer / Load More */}
              {playerPosts.length < totalPosts && (
                <div className="p-4 border-t border-gray-100 bg-gray-50/30 flex justify-center">
                  <button
                    onClick={() => {
                      const nextPage = postPage + 1;
                      setPostPage(nextPage);
                      fetchPlayerPosts(selectedPlayer._id, nextPage);
                    }}
                    disabled={loading}
                    className="px-6 py-2 bg-white border border-[#237e45] text-[#237e45] text-sm font-bold rounded-lg hover:bg-[#237e45] hover:text-white transition-all shadow-sm flex items-center gap-2"
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      "Load More Sessions"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
